/**
 * PromoteToLibraryButton — v3.18.7
 *
 * Small inline button used on Client Profile cards (Past Performance,
 * Certification, Key Personnel). Opens a dialog to confirm options
 * (de-identify, title override, tags) and promotes the per-client
 * record into the consultancy-global Reusable Asset Library.
 *
 * The promoted copy is decoupled from the source — editing one does
 * not affect the other. Endpoint guards against double-promote.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Sparkles, Loader2, BookOpen, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// kind ∈ 'past-performance' | 'certification' | 'key-personnel'
const KIND_CONFIG = {
  'past-performance': {
    sourceTable: 'past_performances',
    endpoint: 'past-performance',
    label: 'Past Performance',
    deidentifyDefault: true,
    deidentifyHint: 'Blanks the client agency, contract number, and POC details on the global copy.',
  },
  'certification': {
    sourceTable: 'certifications',
    endpoint: 'certification',
    label: 'Certification',
    deidentifyDefault: false,
    deidentifyHint: 'Blanks the certificate number on the global copy.',
  },
  'key-personnel': {
    sourceTable: 'key_personnel',
    endpoint: 'key-personnel',
    label: 'Key Personnel',
    deidentifyDefault: false,
    deidentifyHint: "Replaces person's name with initials (e.g., Jane Doe → J.D.) on the global copy.",
  },
};

export default function PromoteToLibraryButton({
  kind,
  recordId,
  defaultTitle = '',
  size = 'sm',
}) {
  const { getAuthHeaders } = useAuth();
  const cfg = KIND_CONFIG[kind];
  const [open, setOpen] = useState(false);
  const [promoted, setPromoted] = useState(null); // null = unknown, true/false after fetch
  const [saving, setSaving] = useState(false);
  const [titleOverride, setTitleOverride] = useState(defaultTitle);
  const [descriptionOverride, setDescriptionOverride] = useState('');
  const [deidentify, setDeidentify] = useState(cfg?.deidentifyDefault ?? false);
  const [tagsRaw, setTagsRaw] = useState('');

  // Lookup promotion status (idempotent guard)
  const fetchStatus = useCallback(async () => {
    if (!recordId || !cfg) return;
    try {
      const res = await fetch(
        `${API_URL}/api/global-kb/promote/status/${cfg.sourceTable}/${recordId}`,
        { headers: getAuthHeaders() },
      );
      if (res.ok) {
        const body = await res.json();
        setPromoted(!!body.promoted);
      } else {
        setPromoted(false);
      }
    } catch (_) {
      setPromoted(false);
    }
  }, [recordId, cfg, getAuthHeaders]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  if (!cfg) return null;

  const handleApply = async () => {
    setSaving(true);
    try {
      const tags = tagsRaw
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      const res = await fetch(
        `${API_URL}/api/global-kb/promote/${cfg.endpoint}/${recordId}`,
        {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            deidentify,
            title_override: titleOverride?.trim() || null,
            description_override: descriptionOverride?.trim() || null,
            tags,
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `HTTP ${res.status}`);
      }
      toast.success(`Promoted to Reusable Asset Library${deidentify ? ' (de-identified)' : ''}`);
      setPromoted(true);
      setOpen(false);
    } catch (e) {
      toast.error(e.message || 'Failed to promote');
    } finally {
      setSaving(false);
    }
  };

  if (promoted) {
    return (
      <Button
        type="button"
        size={size}
        variant="ghost"
        disabled
        className="h-7 px-2 text-xs text-emerald-700 hover:bg-transparent disabled:opacity-100"
        data-testid={`promote-btn-${recordId}-promoted`}
        title="This record is already in the Reusable Asset Library"
      >
        <Check className="w-3.5 h-3.5 mr-1" /> In Library
      </Button>
    );
  }

  return (
    <>
      <Button
        type="button"
        size={size}
        variant="ghost"
        onClick={() => setOpen(true)}
        className={cn(
          'h-7 px-2 text-xs text-violet-700 hover:bg-violet-50 hover:text-violet-800',
        )}
        data-testid={`promote-btn-${recordId}`}
        title="Promote to consultancy-wide Reusable Asset Library"
      >
        <Sparkles className="w-3.5 h-3.5 mr-1" /> Promote
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-lg"
          data-testid={`promote-dialog-${recordId}`}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-violet-600" />
              Promote {cfg.label} to Reusable Asset Library
            </DialogTitle>
            <DialogDescription>
              The promoted copy lives in the consultancy-wide library and is decoupled
              from this Client Profile — editing one does not affect the other.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-medium text-slate-700">Title (optional override)</label>
              <Input
                value={titleOverride}
                onChange={(e) => setTitleOverride(e.target.value)}
                placeholder={defaultTitle}
                data-testid="promote-title-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Description (optional)</label>
              <Textarea
                value={descriptionOverride}
                onChange={(e) => setDescriptionOverride(e.target.value)}
                placeholder="What makes this re-usable across clients? Add context evaluators will want."
                rows={2}
                data-testid="promote-description-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Tags (comma-separated)</label>
              <Input
                value={tagsRaw}
                onChange={(e) => setTagsRaw(e.target.value)}
                placeholder="e.g., cloud, cybersecurity, full-stack"
                data-testid="promote-tags-input"
              />
            </div>
            <label className="flex items-start gap-2 cursor-pointer p-2 rounded border border-slate-200 bg-slate-50 hover:bg-slate-100">
              <input
                type="checkbox"
                checked={deidentify}
                onChange={(e) => setDeidentify(e.target.checked)}
                className="mt-0.5"
                data-testid="promote-deidentify-checkbox"
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-800">De-identify on global copy</p>
                <p className="text-[11px] text-slate-600 mt-0.5">{cfg.deidentifyHint}</p>
              </div>
            </label>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              disabled={saving}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="promote-apply-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" /> Promoting…
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-1" /> Promote to Library
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
